import _ from 'lodash';
import { DateTime } from 'luxon';

import { AppSettings } from '@/app/AppSettings';
import {
  HighlightableString,
  makeHighlightableString,
} from '@/app/log-viewer/highlighting/HighlightableString';
import { HighlightableStringExpandableContainerState } from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { GenericLogEntryExtra, LogEntry } from '@/parsing/LogEntry';

import {
  LogViewerTableRowModel,
  LogViewerTableRowModelData,
  LogViewerTableRowModelDelegate,
  LogViewerTableRowType,
} from '../../LogViewerTableRowModel';
import { HeaderModelData } from './components/Header';
import { RawContentModelData } from './components/RawContent';
import {
  LogEntryCellModel,
  LogEntryCellModelData,
  LogEntryCellModelDelegate,
  LogEntryCellType,
} from './LogEntryCellModel';

export interface LogEntryRowModelData extends LogViewerTableRowModelData {
  module: string;

  header: HeaderModelData;
  rawContent: RawContentModelData;
}

export interface LogEntryRowModelDelegate extends LogViewerTableRowModelDelegate {
  onSelectRowAsBaseForTimeOffset(rowModel: LogEntryRowModel): void;

  onRowDataChange(args: {
    rowModel: LogEntryRowModel;
    change: DeepPartial<LogEntryRowModelData>;
    postProcess: boolean;
  }): void;

  onCellDataChange(args: {
    rowModel: LogEntryRowModel;
    cellModel: LogEntryCellModel;
    change: DeepPartial<LogEntryCellModelData>;
    postProcess: boolean;
  }): void;
}

export interface LogEntryRowModel extends LogViewerTableRowModel, LogEntryCellModelDelegate {
  data: LogEntryRowModelData;
  delegate: LogEntryRowModelDelegate;

  logEntry: LogEntry;

  setBaseTimeOffset(seconds: number): void;

  mainCell: LogEntryCellModel;
  additionalCells: LogEntryCellModel[];

  applySettings(settings: AppSettings): void;
}

export abstract class LogEntryRowModelFactory {
  static make(
    logEntry: LogEntry,
    settings: AppSettings,
    mainCell: LogEntryCellModel,
    delegate: LogEntryRowModelDelegate,
  ) {
    return new LogEntryRowModelImpl(logEntry, settings, mainCell, delegate);
  }
}

class LogEntryRowModelImpl implements LogEntryRowModel {
  data: LogEntryRowModelData;
  delegate: LogEntryRowModelDelegate;

  logEntry: LogEntry;
  stringForFilteringOn: string;

  settings: AppSettings;
  mainCell: LogEntryCellModel;

  private _additionalCells: LogEntryCellModel[] = [];
  get additionalCells(): LogEntryCellModel[] {
    return this._additionalCells;
  }

  constructor(
    logEntry: LogEntry,
    settings: AppSettings,
    mainCell: LogEntryCellModel,
    delegate: LogEntryRowModelDelegate,
  ) {
    const extra = logEntry.extra;
    const genericExtra = extra.generic;

    this.data = {
      type: LogViewerTableRowType.LogEntry,
      indexBeforeApplyingFilters: 0,
      indexAfterApplyingFilters: 0,
      passesFilter: true,
      isSelected: false,
      isBookmarked: false,

      module: logEntry.extra.generic?.module ?? '',

      header: {
        lineNumber: makeHighlightableString(`#${logEntry.index + 1}`),

        dateTime: makeHighlightableString(
          DateTime.fromJSDate(logEntry.date).toFormat('dd.MM.y HH:mm:ss.SSS'),
        ),
        baseTimeOffset: makeHighlightableString(''),
        isBaseTimeOffsetFromThisRow: false,

        module:
          genericExtra && genericExtra.module
            ? makeHighlightableString(
                `🧱 ${genericExtra.module} ${
                  genericExtra.severity ? genericExtra.severity.toLowerCase() : ''
                }`,
              )
            : null,
        thread:
          genericExtra && genericExtra.thread
            ? makeHighlightableString(`🧵 ${genericExtra.thread}`)
            : null,
        sourceFile: genericExtra ? this.sourceFile(genericExtra) : null,
      },

      rawContent: {
        shouldExpandForHighlighting: false,
        expandingState: HighlightableStringExpandableContainerState.Collapsed,
        title: makeHighlightableString('Raw content from the log'),
        linesCount:
          logEntry.originalLines.length > 1
            ? makeHighlightableString(`(${logEntry.originalLines.length} lines)`)
            : null,
        lines: logEntry.originalLines.map((line) => ({
          number: makeHighlightableString(`[line ${line.lineIndex + 1}]`),
          content: makeHighlightableString(line.content),
        })),
      },
    };

    this.settings = settings;
    this.delegate = delegate;
    this.logEntry = logEntry;
    this.stringForFilteringOn = logEntry.content.toLowerCase();

    this.mainCell = mainCell;
    this.mainCell.delegate = this;

    this.additionalCells = [];

    this.applySettings(settings);
  }

  applySettings(settings: AppSettings) {
    this.settings = settings;
    this.mainCell.applySettings(settings);

    this.additionalCells.forEach((cell) => {
      cell.applySettings(settings);
    });
  }

  set additionalCells(models: LogEntryCellModel[]) {
    this._additionalCells = _.clone(models);
    this._additionalCells.forEach((model) => {
      model.delegate = this;
      model.applySettings(this.settings);
    });
  }

  setBaseTimeOffset(seconds: number) {
    const dt = seconds.toFixed(3);
    this.data.header.baseTimeOffset = makeHighlightableString(`(𝚫 ${dt} sec)`);
  }

  private sourceFile(extra: GenericLogEntryExtra): HighlightableString | null {
    if (extra.fileName && extra.fileFunction && extra.fileLine) {
      const filePath = extra.fileName;
      const fileName = _.nth(filePath.split('/'), -1);
      const place = `📃 ${fileName} ${extra.fileFunction}:${extra.fileLine}`;
      return makeHighlightableString(place);
    }
    return null;
  }

  // MARK: - LogViewerTableLogEntryCellModelDelegate

  onLogEntryCellModelChange(args: {
    cellModel: LogEntryCellModel;
    change: { type?: LogEntryCellType | undefined };
    postProcess: boolean;
  }): void {
    this.delegate.onCellDataChange({
      rowModel: this,
      ...args,
    });
  }
}
